/**
 * DeadlineBar — animated countdown progress bar for time-bounded deal phases.
 *
 * Renders a horizontal fill bar that visually drains as the deadline
 * approaches, plus a human-readable "X gun Y saat kaldi" label. The fill
 * uses a 4-stage colour palette so the user gets a strong at-a-glance
 * sense of urgency without having to read the numeric countdown:
 *
 *   > 60%   → emerald   (plenty of time, calm)
 *   40-60%  → yellow    (halfway, watch the clock)
 *   20-40%  → orange    (running short, act soon)
 *   <  20%  → rose/red  (urgent, last stretch)
 *   = 0%    → muted     ("expired" badge instead of bar)
 *
 * The "flowing" effect is a CSS-driven gradient sweep on the filled
 * portion (similar to a download progress bar) so the user gets
 * continuous motion even when the percentage doesn't change visibly
 * within a single second.
 *
 * Update cadence is adaptive — once-per-second when remaining < 1h,
 * once-per-30s when < 1d, once-per-5min when > 1d. This keeps mobile
 * battery usage trivial on long-running funding windows while still
 * feeling alive in the final stretch.
 *
 * Both buyer and seller render the same component; nothing here is
 * role-aware. The parent `DealDetailPage` decides which deadline is
 * relevant based on the deal's current FSM state and passes it in.
 */

import { useEffect, useMemo, useState } from 'react';
import { Clock3, ShieldAlert, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

interface DeadlineBarProps {
  /** ISO timestamp the user must act by. */
  deadline: string;
  /**
   * ISO timestamp the phase started (e.g. `deal.createdAt` for the
   * funding window, or the previous transition timestamp for delivery).
   * Used to compute the FULL phase length so the bar visualises
   * "elapsed of total" instead of just "remaining". If omitted we
   * default to "deadline minus 24 hours" which is fine for short windows.
   */
  startedAt?: string;
  /** Short label shown above the bar — e.g. "Funding window". */
  label: string;
  /** Optional extended hint shown next to the time, e.g. "buyer must fund". */
  hint?: string;
  /** Custom expired-state copy. Defaults to a generic "Expired". */
  expiredLabel?: string;
  /** When true the bar collapses to a static "expired" state regardless of math. */
  forceExpired?: boolean;
  className?: string;
}

interface CountdownState {
  remainingMs: number;
  totalMs: number;
  pct: number; // 0..100, fraction REMAINING (not elapsed)
  parts: { days: number; hours: number; minutes: number; seconds: number };
}

const ONE_SEC = 1_000;
const ONE_MIN = 60 * ONE_SEC;
const ONE_HOUR = 60 * ONE_MIN;
const ONE_DAY = 24 * ONE_HOUR;

function compute(deadlineIso: string, startedAtIso: string | undefined): CountdownState | null {
  const deadlineMs = new Date(deadlineIso).getTime();
  if (Number.isNaN(deadlineMs)) return null;
  const now = Date.now();
  const remainingMs = deadlineMs - now;

  // Default phase length to 24h if no start anchor — short enough that
  // the bar still drains meaningfully but never wraps to 0% on day 1
  // of a 7-day window.
  const startMs = startedAtIso
    ? new Date(startedAtIso).getTime()
    : deadlineMs - ONE_DAY;
  const totalMs = Math.max(deadlineMs - startMs, ONE_MIN);

  const clampedRemaining = Math.max(remainingMs, 0);
  const pct = Math.min(100, Math.max(0, (clampedRemaining / totalMs) * 100));

  const totalSeconds = Math.floor(clampedRemaining / 1000);
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return {
    remainingMs,
    totalMs,
    pct,
    parts: { days, hours, minutes, seconds },
  };
}

/**
 * Pick a tick interval based on how much time is left. Avoids burning
 * CPU once-per-second on a 7-day funding window while still rendering
 * the seconds counter live in the final hour.
 */
function pickInterval(remainingMs: number): number {
  if (remainingMs <= 0) return ONE_SEC * 60; // expired — slow loop, just for re-renders
  if (remainingMs < ONE_HOUR) return ONE_SEC;
  if (remainingMs < ONE_DAY) return ONE_SEC * 30;
  return ONE_SEC * 60 * 5;
}

function formatRemaining(parts: CountdownState['parts']): string {
  const { days, hours, minutes, seconds } = parts;
  if (days > 0) return `${days}g ${hours}s`;
  if (hours > 0) return `${hours}s ${minutes}d`;
  if (minutes > 0) return `${minutes}d ${seconds}sn`;
  return `${seconds}sn`;
}

export function DeadlineBar({
  deadline,
  startedAt,
  label,
  hint,
  expiredLabel,
  forceExpired = false,
  className,
}: DeadlineBarProps): JSX.Element | null {
  const [state, setState] = useState<CountdownState | null>(() =>
    compute(deadline, startedAt),
  );

  useEffect(() => {
    setState(compute(deadline, startedAt));
    let cancelled = false;

    const tick = (): void => {
      if (cancelled) return;
      const next = compute(deadline, startedAt);
      setState(next);
      const delay = next ? pickInterval(next.remainingMs) : ONE_SEC * 60;
      timer = window.setTimeout(tick, delay);
    };

    let timer = window.setTimeout(tick, pickInterval(state?.remainingMs ?? ONE_HOUR));
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // We deliberately depend only on the inputs, not on `state` —
    // recomputing the schedule on every state change would burn CPU.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadline, startedAt]);

  const expired = forceExpired || (state !== null && state.remainingMs <= 0);

  /**
   * 4-stage tone mapping. Thresholds chosen so each stage covers a
   * meaningful chunk of the timeline:
   *   - 60-100% → 40% of the timeline (the relaxed phase)
   *   - 40-60%  → 20% (the "watch the clock" phase)
   *   - 20-40%  → 20% (the "act soon" phase)
   *   - 0-20%   → 20% (the urgent last stretch — red)
   */
  const tone = useMemo<'good' | 'warn' | 'caution' | 'danger' | 'muted'>(() => {
    if (expired) return 'muted';
    if (!state) return 'muted';
    if (state.pct > 60) return 'good';
    if (state.pct > 40) return 'warn';
    if (state.pct > 20) return 'caution';
    return 'danger';
  }, [state, expired]);

  if (!state) return null;

  // Tone → Tailwind tokens. Keep this map small and explicit so the
  // designer can read the colour palette in one glance.
  const fillClass = {
    good: 'bg-gradient-to-r from-emerald-400 via-emerald-300 to-emerald-500',
    warn: 'bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500',
    caution: 'bg-gradient-to-r from-orange-500 via-orange-400 to-orange-600',
    danger: 'bg-gradient-to-r from-rose-500 via-rose-400 to-rose-600',
    muted: 'bg-text-muted/40',
  }[tone];

  const labelToneClass = {
    good: 'text-emerald-300',
    warn: 'text-yellow-300',
    caution: 'text-orange-400',
    danger: 'text-rose-400',
    muted: 'text-text-muted',
  }[tone];

  const Icon = expired ? ShieldAlert : tone === 'good' ? Sparkles : Clock3;

  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-md border border-border/40 bg-bg-secondary/40 p-3',
        className,
      )}
      role="timer"
      aria-live="polite"
    >
      <div className="flex items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2 text-text-muted">
          <Icon className={cn('h-3.5 w-3.5', labelToneClass)} aria-hidden />
          <span className="uppercase tracking-wide">{label}</span>
        </div>
        <div className={cn('font-semibold tabular-nums', labelToneClass)}>
          {expired ? expiredLabel ?? 'Sure doldu' : formatRemaining(state.parts)}
        </div>
      </div>

      {/* Progress track */}
      <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-bg-tertiary/60">
        <div
          className={cn(
            'absolute inset-y-0 left-0 rounded-full',
            'transition-[width] duration-1000 ease-linear',
            fillClass,
          )}
          style={{ width: `${state.pct}%` }}
        >
          {/* Flowing shimmer overlay — pure CSS, no JS animation cost. */}
          {!expired && (
            <span
              aria-hidden
              className={cn(
                'absolute inset-0 -translate-x-full',
                'animate-deadline-shimmer',
                'bg-gradient-to-r from-transparent via-white/30 to-transparent',
              )}
            />
          )}
        </div>
      </div>

      {hint && !expired && (
        <p className="text-[10px] text-text-muted">{hint}</p>
      )}
    </div>
  );
}
